import '@fontsource-variable/inter';
import './styles/app.css';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { RouterProvider } from '@tanstack/react-router';
import { LucideProvider } from 'lucide-react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { api, setSignedOutHandler } from './api/client.js';
import { keys } from './api/hooks.js';
import { persister, queryClient, shouldPersist } from './api/queryClient.js';
import { revalidateRestoredCache } from './api/realtime-resync.js';
import { ToastProvider } from './components/Toast.js';
import { checkAppFont } from './lib/fontCheck.js';
import { trackInputModality } from './lib/inputModality.js';
import { initSentry } from './lib/sentry.js';
import { bootTheme } from './lib/theme.js';
import { preloadApp, router } from './router.js';

initSentry();
// The shell and chat are their own chunks so portal and share-link visitors skip them; everyone else
// starts fetching them now rather than after the router has matched.
if (!/^\/(portal|s|join|note)\//.test(window.location.pathname)) void preloadApp().catch(() => undefined);
// The theme is on <html> before the first paint, so nothing flashes in the wrong colours.
bootTheme();
// Keyboard or pointer, for the message box's keyboard-only focus line (lib/inputModality.ts).
trackInputModality();
// Inter must never fall back silently (RESTYLE_BRIEF.md "Font"): warn in the console if it did not load.
void checkAppFont(typeof document !== 'undefined' ? document.fonts : undefined);

/**
 * A 401 usually means signed out. It can also mean the workspace was scheduled for deletion since the
 * cached `me` was fetched (docs/adr/0023-export-and-deletion.md): then owners still get `me`, and putting
 * it in the cache swaps the app to the pending-deletion screen instead of sending them to sign-in.
 */
let checkingMe = false;
setSignedOutHandler(() => {
  const here = window.location.pathname;
  if (['/sign-in', '/auth/verify', '/delete-account', '/account-deletion'].includes(here) || here.startsWith('/s/') || here.startsWith('/join/') || checkingMe) return;
  checkingMe = true;
  void api('me')
    .then((me) => {
      if (me.workspace.status === 'pending_deletion') queryClient.setQueryData(keys.me, me);
      else void router.navigate({ to: '/sign-in' });
    })
    .catch(async () => {
      // Or the person's own account was just scheduled for deletion (docs/adr/0040): show that screen.
      const session = await api('session').catch(() => null);
      if (session?.accountDeletion) {
        queryClient.setQueryData(keys.session, session);
        void router.navigate({ to: '/account-deletion' });
      } else void router.navigate({ to: '/sign-in' });
    })
    .finally(() => (checkingMe = false));
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 7 * 24 * 60 * 60 * 1000,
        buster: 'v1',
        dehydrateOptions: { shouldDehydrateQuery: (q) => q.state.status === 'success' && shouldPersist(q.queryKey) },
      }}
      // Cache first, then the network: the restored copy may have missed live events (realtime-resync.ts).
      onSuccess={() => revalidateRestoredCache(queryClient)}
    >
      {/* Lucide at 1.9 stroke everywhere (RESTYLE_BRIEF.md "Crispness"); an icon that sets its own width keeps it. */}
      <LucideProvider strokeWidth={1.9}>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </LucideProvider>
    </PersistQueryClientProvider>
  </StrictMode>,
);
